import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CrmAuth, FormAgainItem } from './crmApi';
import LeadPanel, { ResumeButton } from './LeadPanel';
import { Empty, Head, Spinner, StageChip, fmtPhone, fmtWhen, inputCls, useCrmMe } from './ui';

// ── Forms again ─────────────────────────────────
// A website form (/p2p, /analyst) filled by a number we already know: a lead the
// CRM has, which went back to its salesperson, or a current student, who has no
// lead. Admin and the Lead Managers hear at once and see the details here.
// Mirrors the app's crm_forms_screen.dart.

const FORM_LABEL: Record<string, string> = { p2p: 'P2P', analyst: 'Analyst' };
const WHICH = [['', 'All forms'], ['p2p', 'P2P'], ['analyst', 'Analyst']];

const nowWith = (f: FormAgainItem) => {
  if (!f.leadId) return f.leadNo ? 'The lead was deleted' : '';
  if (f.ownerName) return `Now with: ${f.ownerName}`;
  return f.waiting ? 'Waiting for someone free' : 'No one has it';
};

export const CrmFormsAgain: React.FC<{ auth: CrmAuth; embedded?: boolean }> = ({ auth, embedded }) => {
  const { me } = useCrmMe(auth);
  const [q, setQ] = useState('');
  const [form, setForm] = useState('');
  const [data, setData] = useState<{ today: number; items: FormAgainItem[] } | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setData(await crm.formsAgain(auth, { q: q.trim(), form })); } catch (e: any) { toast.error(e.message); }
  }, [auth, q, form]);
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  return (
    <div className="space-y-4">
      <Head embedded={embedded} title="Forms again" onRefresh={load}
        sub={`People we already know who filled a website form${data ? ` · ${data.today} today` : ''}`} />
      <div className="flex flex-wrap items-center gap-2">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, phone or email" className={`${inputCls} max-w-xs`} />
        <div className="flex gap-1 p-1 rounded-xl bg-white border border-gray-100" role="group" aria-label="Which form">
          {WHICH.map(([k, label]) => (
            <button key={k} onClick={() => setForm(k)} aria-pressed={form === k} className="px-3 py-1.5 rounded-lg text-xs font-bold"
              style={form === k ? { background: '#2b2240', color: '#fff' } : { color: '#6b7280' }}>{label}</button>
          ))}
        </div>
      </div>
      {!data ? <Spinner /> : data.items.length === 0 ? (
        <Empty icon="🔁" title={q || form ? 'None match' : 'No repeat forms yet'} sub="When someone we already know fills the P2P or Analyst form, they appear here." />
      ) : (
        <div className="grid gap-2 lg:grid-cols-2">
          {data.items.map(f => (
            <div key={f.id} className="bg-white border border-gray-100 rounded-2xl px-4 py-3 space-y-1.5">
              <div className="flex items-start justify-between gap-3">
                <p className="font-bold text-gray-800 min-w-0">
                  {f.kind === 'student' ? '🎓' : '🔁'} {f.name || 'No name'} <span className="text-sm font-mono font-semibold text-gray-500">{fmtPhone(f.phone)}</span>
                </p>
                <span className="text-[11px] text-gray-400 whitespace-nowrap">{fmtWhen(f.at)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{FORM_LABEL[f.form] || f.form} form</span>
                {f.kind === 'student' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Current student · no lead made</span>}
                {f.held && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Stayed as it was</span>}
                {f.stage && <StageChip stage={f.stage as any} small />}
              </div>
              <p className="text-xs text-gray-600">{[f.email, f.degree, f.passedOutYear].filter(Boolean).join(' · ') || 'No email given'}</p>
              {f.skills.length > 0 && <p className="text-xs text-gray-600">{f.skills.map(s => (s.level ? `${s.name} (${s.level})` : s.name)).join(' · ')}</p>}
              {f.kind === 'repeat' && (
                <p className="text-xs text-gray-500">
                  Before: {f.beforeStage || '—'}{f.beforeOwner ? ` · with ${f.beforeOwner}` : ''}{f.enquiries > 1 ? ` · enquiry ${f.enquiries}` : ''}
                  {nowWith(f) && <><br /><b className="text-gray-700">{nowWith(f)}</b></>}
                </p>
              )}
              {(f.oldName || f.oldEmail) && (
                <p className="text-[11px] text-gray-400">Replaced: {[f.oldName && `name "${f.oldName}"`, f.oldEmail && `email ${f.oldEmail}`].filter(Boolean).join(', ')}</p>
              )}
              <div className="flex flex-wrap gap-2 pt-0.5">
                <a href={`tel:+91${f.phone}`} className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-green-600">Call</a>
                {f.leadId && <button onClick={() => setOpen(f.leadId)} className="px-3 py-1.5 rounded-lg text-[11px] font-bold border border-gray-200 text-gray-700 hover:bg-gray-50">Open lead #{f.leadNo}</button>}
                {f.leadId && f.resume && <ResumeButton auth={auth} leadId={f.leadId} resume={f.resume} compact />}
              </div>
            </div>
          ))}
        </div>
      )}
      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

export default CrmFormsAgain;
